import { T, useGT } from 'gt-react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  MAX_TASK_TYPE_METRIC_LABEL_LENGTH,
  TASK_TYPE_METRIC_KINDS,
  type TaskTypeMetricKind,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { anchorMetricFieldOptions } from 'src/components/tasks/anchorMetricFields';
import { TaskTypeSection } from './TaskTypeEditorSections';
import type { ProcedureInputSchemaStatus } from './procedureParameterBinding';
import {
  canAddMetricDraft,
  metricDraftProblem,
  moveMetricDraft,
  newMetricDraft,
  effectiveMetricFields,
  withMetricKind,
  type MetricDraftProblem,
  type MetricFieldOption,
  type TaskTypeMetricDraft,
} from './taskTypeMetricsDraft';

function isMetricKind(value: string): value is TaskTypeMetricKind {
  return (TASK_TYPE_METRIC_KINDS as readonly string[]).includes(value);
}

function useMetricKindLabels(): Record<TaskTypeMetricKind, string> {
  const gt = useGT();
  return {
    open_count: gt('Open tasks', {
      context: 'task type metric card kind option: count of open tasks',
    }),
    unassigned: gt('Unassigned', {
      context: 'task type metric card kind option: open tasks with no assignee',
    }),
    aging: gt('Aging', {
      context:
        'task type metric card kind option: open tasks by age in days (0-2, 3-7, 8-14, 15+)',
    }),
    sum: gt('Sum of a field', {
      context:
        'task type metric card kind option: total of a numeric input field across open tasks',
    }),
  };
}

function MetricProblem({ problem }: { problem: MetricDraftProblem | null }) {
  if (problem === null) return null;
  return (
    <p className="text-xs text-destructive">
      {problem === 'missing_field' && (
        <T context="task type metric card validation error">
          Pick the field this card totals.
        </T>
      )}
      {problem === 'unknown_field' && (
        <T context="task type metric card validation error">
          This card can't total that field. Pick a number or currency field, or
          an amount on the linked record.
        </T>
      )}
      {problem === 'label_too_long' && (
        <T context="task type metric card validation error">
          The title is too long.
        </T>
      )}
    </p>
  );
}

function SumFieldSelect({
  draft,
  numericFields,
  disabled,
  onChange,
}: {
  draft: TaskTypeMetricDraft;
  numericFields: readonly MetricFieldOption[];
  disabled: boolean;
  onChange: (next: TaskTypeMetricDraft) => void;
}) {
  const gt = useGT();
  return (
    <Select
      value={draft.field}
      disabled={disabled || numericFields.length === 0}
      onValueChange={(field) => onChange({ ...draft, field })}
    >
      <SelectTrigger
        className="h-8 w-48"
        aria-label={gt('Field to total', {
          context: 'accessible label for the sum metric card field picker',
        })}
      >
        <SelectValue
          placeholder={
            numericFields.length === 0
              ? gt('No number fields', {
                  context:
                    'placeholder when the task type input schema has no number or currency field to total',
                })
              : gt('Pick a field', {
                  context: 'placeholder for the sum metric card field picker',
                })
          }
        />
      </SelectTrigger>
      <SelectContent>
        {numericFields.map((field) => (
          <SelectItem key={field.fieldName} value={field.fieldName}>
            {field.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MetricRow({
  draft,
  index,
  count,
  numericFields,
  fieldOptions,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  draft: TaskTypeMetricDraft;
  index: number;
  count: number;
  /** Null until the input schema loads (unknown fields are not flagged yet). */
  numericFields: readonly MetricFieldOption[] | null;
  /** The fields a sum card can pick right now. */
  fieldOptions: readonly MetricFieldOption[];
  disabled: boolean;
  onChange: (next: TaskTypeMetricDraft) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const kindLabels = useMetricKindLabels();
  return (
    <li className="flex flex-col gap-1 rounded border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={draft.kind}
          disabled={disabled}
          onValueChange={(kind) => {
            if (isMetricKind(kind)) onChange(withMetricKind(draft, kind));
          }}
        >
          <SelectTrigger
            className="h-8 w-40"
            aria-label={gt('Card type', {
              context: 'accessible label for the metric card kind picker',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TASK_TYPE_METRIC_KINDS.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {kindLabels[kind]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {draft.kind === 'sum' && (
          <SumFieldSelect
            draft={draft}
            numericFields={fieldOptions}
            disabled={disabled}
            onChange={onChange}
          />
        )}
        <Input
          value={draft.label}
          disabled={disabled}
          maxLength={MAX_TASK_TYPE_METRIC_LABEL_LENGTH}
          onChange={(event) =>
            onChange({ ...draft, label: event.target.value })
          }
          className="h-8 min-w-32 flex-1 text-xs"
          placeholder={gt('Title (optional)', {
            context:
              'placeholder for an optional custom title on a task type metric card',
          })}
          aria-label={gt('Card title', {
            context: 'accessible label for a metric card title input',
          })}
        />
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled || index === 0}
          onClick={() => onMove(-1)}
          aria-label={gt('Move card up', {
            context: 'button in the task type metric cards editor',
          })}
        >
          <ArrowUpIcon className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled || index === count - 1}
          onClick={() => onMove(1)}
          aria-label={gt('Move card down', {
            context: 'button in the task type metric cards editor',
          })}
        >
          <ArrowDownIcon className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={disabled}
          onClick={onRemove}
          aria-label={gt('Remove card', {
            context: 'button in the task type metric cards editor',
          })}
        >
          <TrashIcon className="size-3.5" />
        </Button>
      </div>
      <MetricProblem problem={metricDraftProblem(draft, numericFields)} />
    </li>
  );
}

/**
 * The metric cards shown above this type's queue on a Task Page: add,
 * remove, reorder, pick the kind, the numeric field a sum card totals, and
 * an optional title.
 */
export function TaskTypeMetricsSection({
  drafts,
  schemaStatus,
  inputFields,
  anchorEntityType,
  disabled,
  onChange,
}: {
  drafts: TaskTypeMetricDraft[];
  schemaStatus: ProcedureInputSchemaStatus;
  inputFields: readonly MetricFieldOption[];
  /** The draft's anchor type: its linked-record amounts can be totalled too. */
  anchorEntityType: string;
  disabled: boolean;
  onChange: (next: TaskTypeMetricDraft[]) => void;
}) {
  const gt = useGT();
  const anchorFields = anchorMetricFieldOptions(anchorEntityType, gt);
  // Unknown until the schema loads; a failed load flags nothing either.
  const numericFields = effectiveMetricFields(
    schemaStatus,
    inputFields,
    anchorFields,
  );
  return (
    <TaskTypeSection
      title={<T context="task type editor section title">Metric cards</T>}
      description={
        <T context="task type editor metric cards section description">
          The cards above this type's queue on a task page. They count the open
          tasks each viewer can see. A sum card totals a number or currency
          field from the input schema, or an amount on the linked sales order or
          invoice.
        </T>
      }
    >
      {drafts.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T context="empty state in the task type metric cards editor">
            No cards. The task page shows the queue alone.
          </T>
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {drafts.map((draft, index) => (
            <MetricRow
              key={draft.clientId}
              draft={draft}
              index={index}
              count={drafts.length}
              numericFields={numericFields}
              fieldOptions={numericFields ?? anchorFields}
              disabled={disabled}
              onChange={(next) =>
                onChange(
                  drafts.map((candidate) =>
                    candidate.clientId === draft.clientId ? next : candidate,
                  ),
                )
              }
              onMove={(delta) =>
                onChange(moveMetricDraft(drafts, index, delta))
              }
              onRemove={() =>
                onChange(
                  drafts.filter(
                    (candidate) => candidate.clientId !== draft.clientId,
                  ),
                )
              }
            />
          ))}
        </ul>
      )}
      <div>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || !canAddMetricDraft(drafts)}
          onClick={() => onChange([...drafts, newMetricDraft()])}
        >
          <PlusIcon className="size-3.5" />
          <T context="button adding a metric card to a task type">Add Card</T>
        </Button>
      </div>
    </TaskTypeSection>
  );
}
